<script>
import utils from '@/utils/common';
import taskApi from '../../../api/task';

import TaskViewUsersDispatcher from './TaskViewUsersDispatcher.vue';
import TaskViewDateDispatcher from './TaskViewDateDispatcher.vue';
import TaskViewDescriptionDispatcher from './TaskViewDescriptionDispatcher.vue';
import TaskViewCheckListDispatcher from './TaskViewCheckListDispatcher.vue';
import TaskViewStatusDispatcher from './TaskViewStatusDispatcher.vue';
import TaskViewPriorityDispatcher from './TaskViewPriorityDispatcher.vue';

export default {
  name: 'TaskViewActionsDispatcher',
  controller: null,
  components: {
    TaskViewUsersDispatcher,
    TaskViewDateDispatcher,
    TaskViewDescriptionDispatcher,
    TaskViewCheckListDispatcher,
    TaskViewStatusDispatcher,
    TaskViewPriorityDispatcher,
  },
  props: {
    tasks: Array,
  },
  data() {
    return {
      action: null,
      mode: null,
    };
  },
  computed: {
    components() {
      return {
        author: 'TaskViewUsersDispatcher',
        manager: 'TaskViewUsersDispatcher',
        members: 'TaskViewUsersDispatcher',
        dateStart: 'TaskViewDateDispatcher',
        dateFinish: 'TaskViewDateDispatcher',
        description: 'TaskViewDescriptionDispatcher',
        checklist: 'TaskViewCheckListDispatcher',
        status: 'TaskViewStatusDispatcher',
        priority: 'TaskViewPriorityDispatcher',
        deleteMembers: 'TaskViewUsersDispatcher',
        deleteParticipants: 'TaskViewUsersDispatcher',
      };
    },

    component() {
      return this.components[this.action] ?? null;
    },

    selectedTasksWord() {
      return utils.pluralWord(this.tasks.length, 'задачу', 'задачи', 'задач');
    },
  },
  methods: {
    async open(action, mode) {
      this.action = action;
      this.mode = mode;

      if (['delete', 'open', 'close'].includes(action)) {
        this.handleBaseActions();
      }

      return new Promise((resolve, reject) => {
        this.$options.controller = { resolve, reject };
      });
    },

    async handleBaseActions() {
      const operations = {
        delete: [{ delete: true }, 'удалить'],
        open: [{ isClosed: false }, 'открыть'],
        close: [{ isClosed: true }, 'закрыть'],
      };

      const [value, actionName] = operations[this.action];
      const question = this.action === 'delete'
        ? `Вы уверены, что хотите ${actionName} ${this.selectedTasksWord}? Эту операцию невозможно отменить.`
        : `Вы уверены, что хотите ${actionName} ${this.selectedTasksWord}?`;

      let text;
      if (this.action === 'delete') text = utils.pluralWord(this.tasks.length, 'задача удалена', 'задачи удалены', 'задач удалено');
      if (this.action === 'open') text = utils.pluralWord(this.tasks.length, 'задача открыта', 'задачи открыты', 'задач открыто');
      if (this.action === 'close') text = utils.pluralWord(this.tasks.length, 'задача закрыта', 'задачи закрыты', 'задач закрыто');
      const params = { result: value, onSuccess: () => { this.$push({ text }); } };

      const result = await this.$confirm(question) ? params : null;
      this.dispatch(result);
    },

    async dispatch(params) {
      if (params && !this.mode && this.action !== 'deleteMembers' && this.action !== 'deleteParticipants') {
        const { result, onSuccess } = params;

        if (result?.delete) {
          await Promise.all(this.tasks.map(t => taskApi.delete(t.id, true)));
        } else if (result) {
          await Promise.all(this.tasks.map(t => taskApi.update(t.id, result, true)));
        }
        onSuccess();
      }

      if (params && this.mode === 'set') {
        const { result, onSuccess } = params;

        await Promise.all(this.tasks.map(t => taskApi.update(t.id, result, true)));
        onSuccess();
      }

      if (params && this.action === 'deleteMembers') {
        const { result, onSuccess } = params;

        const filteredTasks = this.tasks.filter(t => {
          const members = t.members.filter(m => result.deleteMembers.includes(m['@id']));
          return members.length > 0;
        });

        if (filteredTasks.length) {
          const taskMembers = filteredTasks.map(t => {
            const newMembers = t.members.filter(m => !result.deleteMembers.includes(m['@id'])).map(m => m['@id']);
            return taskApi.update(t.id, { members: newMembers }, true);
          });
          await Promise.all(taskMembers);
          onSuccess();
        }
      }

      if (params && this.action === 'deleteParticipants') {
        const { result, onSuccess } = params;

        const filteredTasks = this.tasks.filter(t => {
          const members = t.members.filter(m => result.deleteParticipants.includes(m['@id']));

          if (t.manager && result.deleteParticipants.includes(t.manager['@id'])) {
            members.push(t.manager);
          }

          if (result.deleteParticipants.includes(t.author['@id'])) {
            members.push(t.author);
          }

          return members.length > 0;
        });

        if (filteredTasks.length) {
          const taskParticipants = filteredTasks.map(t => {
            const data = {};
            if (t.manager && result.deleteParticipants.includes(t.manager['@id'])) {
              data.manager = null;
            }

            if (result.deleteParticipants.includes(t.author['@id'])) {
              data.author = this.$store.state.auth.user['@id'];
            }

            data.members = t.members
              .filter(m => !result.deleteParticipants.includes(m['@id']))
              .map(m => m['@id']);

            return taskApi.update(t.id, data, true);
          });
          await Promise.all(taskParticipants);
          onSuccess();
        }
      }

      if (params && this.mode === 'add') {
        const { result, onSuccess } = params;
        const tasksMembers = this.tasks.map(t => taskApi.update(t.id, { members: [...t.members.map(m => m['@id']), ...result.members] }, true));
        await Promise.all(tasksMembers);
        onSuccess();
      }

      this.$options.controller.resolve(params !== null);
      this.action = null;
      this.mode = null;
    },
  },
};
</script>

<template lang="pug">
component(:is="component" :action="action" :mode="mode" :tasks="tasks" :isOpened="!!component" @close="dispatch(null)" @submit="dispatch")
</template>

<style scoped lang="scss">

</style>
